import { useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, type MiloStatus } from "@keydials/api-client";
import { api, getAccessToken, useAuth } from "../../auth/AuthContext";
import { Icon } from "../Icon";
import { useMiloConversation } from "./useMiloConversation";
import { currentInstanceId, emptyMiloChat, offersTopUp, packPriceLine, poolLine, showsPlusNote, withError, withReply, withUserMessage, type MiloChatState } from "./miloChat";

/**
 * Milo, on the web (KEY-704): a button in the bottom-right corner on every page, opening a panel you talk to
 * Milo in. Global by decision (Tim, 30 Sep 2026): one conversation wherever you are, rather than a tab on each
 * dial. The dial you're on rides with each turn so "the MOT" needs no "on which dial?"; anywhere else Milo asks
 * or looks it up.
 *
 * Bottom-right rather than the header because the header has no width to spare (the builder was moved out of
 * it for the same reason), and rather than the feedback pill's corner so the two never overlap. Same z-index as
 * the other floating panels, so a real modal still wins.
 *
 * The panel remembers the conversation for the tab (sessionStorage), and rebuilds the visible messages from the
 * server on reload; the server holds the transcript, this holds only what is shown.
 */

const CONVERSATION_KEY = "kdu.milo.conversation";
const PANEL_KEY = "kdu.milo.panel";

const fieldStyle: CSSProperties = {
  flex: 1,
  resize: "none",
  font: "inherit",
  fontSize: 14.5,
  lineHeight: 1.4,
  padding: "10px 12px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface-2)",
  color: "var(--text)",
  outline: "none",
};

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // Private windows and blocked storage: the panel simply forgets on reload.
  }
}

export function MiloDock() {
  const { me } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<MiloStatus | null>(null);
  const [open, setOpen] = useState(() => read(PANEL_KEY) === "open");
  const [chat, setChat] = useState<MiloChatState>(emptyMiloChat);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  // "Not now" on the Milo Plus note: hidden until the panel next mounts (it is a note, not a nag).
  const [plusNoteHidden, setPlusNoteHidden] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // KEY-850: conversation mode — talk hands-free, hear the replies. What is heard goes through `send` like typing.
  const talk = useMiloConversation((heard) => void send(heard));
  const stopTalking = talk.stop;

  // Whether Milo is on here, and where the pool stands — the pool a turn from THIS page would spend, which is
  // the dial's organisation's when a dial is open. Asked at sign-in and whenever the page changes, and again
  // after every turn, because a turn is what moves the count.
  const instanceId = currentInstanceId(location.pathname);
  const refreshStatus = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      setStatus(await api.miloStatus(token, instanceId));
    } catch {
      setStatus(null);
    }
  }, [instanceId]);

  useEffect(() => {
    if (me) void refreshStatus();
  }, [me, refreshStatus]);

  // The conversation this tab was having, rebuilt from the server. Gone (expired, or somebody else's) means
  // start afresh, quietly.
  useEffect(() => {
    const token = getAccessToken();
    const id = read(CONVERSATION_KEY);
    if (!me || !token || !id) return;
    void api
      .miloConversation(token, id)
      .then((c) =>
        setChat({
          conversationId: c.id,
          messages: c.messages.map((m) => ({ role: m.role, text: m.text })),
          proposed: c.proposedAction ?? null,
          turnsLeft: c.turnsLeft,
        }),
      )
      .catch(() => write(CONVERSATION_KEY, null));
  }, [me]);

  useEffect(() => {
    write(PANEL_KEY, open ? "open" : null);
    if (open) inputRef.current?.focus();
    else stopTalking();
  }, [open, stopTalking]);

  // Going to another page, or leaving the app, ends conversation mode: it never carries on out of sight.
  useEffect(() => () => stopTalking(), [location.pathname, stopTalking]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [chat.messages.length, chat.proposed, busy]);

  const apply = useCallback((next: MiloChatState) => {
    setChat(next);
    write(CONVERSATION_KEY, next.conversationId);
  }, []);

  /** Sends what was typed, or — in conversation mode — what was heard. */
  async function send(spoken?: string) {
    const text = (spoken ?? draft).trim();
    const token = getAccessToken();
    if (!text || !token || busy) {
      // Heard words with nowhere to go (a turn already in flight): stop rather than wait on a reply that never comes.
      if (spoken !== undefined) talk.stop();
      return;
    }
    if (spoken === undefined) setDraft("");
    talk.sending();
    setBusy(true);
    const asked = withUserMessage(chat, text);
    setChat(asked);
    try {
      let reply;
      try {
        reply = await api.miloTurn(token, { message: text, instanceId }, asked.conversationId);
      } catch (e) {
        // A conversation that has ended is not the person's problem: start another with the same words.
        if (e instanceof ApiError && (e.status === 404 || e.status === 409) && asked.conversationId) {
          reply = await api.miloTurn(token, { message: text, instanceId }, null);
        } else {
          throw e;
        }
      }
      apply(withReply(asked, reply));
      talk.replied(reply.reply, reply.turnsLeft, !!reply.proposedAction);
    } catch (e) {
      apply(withError(asked, e instanceof ApiError ? e.message : "Milo couldn't answer just now. Try again in a moment."));
      talk.failed();
    } finally {
      setBusy(false);
      void refreshStatus();
      inputRef.current?.focus();
    }
  }

  async function answer(confirm: boolean, choiceId?: string) {
    const token = getAccessToken();
    if (!token || !chat.conversationId || !chat.proposed || busy) return;
    talk.sending();
    setBusy(true);
    try {
      const reply = confirm
        ? await api.miloConfirm(token, chat.conversationId, chat.proposed.id, choiceId)
        : await api.miloDecline(token, chat.conversationId, chat.proposed.id);
      apply(withReply({ ...chat, proposed: null }, reply));
      talk.replied(reply.reply, reply.turnsLeft, !!reply.proposedAction);
    } catch (e) {
      apply(withError(chat, e instanceof ApiError ? e.message : "That didn't go through. Try again."));
      talk.failed();
    } finally {
      setBusy(false);
    }
  }

  function startAfresh() {
    apply(emptyMiloChat);
    inputRef.current?.focus();
  }

  // KEY-715: "Turn Milo off here" puts Milo away for the whole household on this dial. The button
  // disappears with the next status, and a dial admin can bring him back from the dial's Display settings.
  async function dismiss() {
    const token = getAccessToken();
    if (!token || !instanceId || busy) return;
    setBusy(true);
    try {
      await api.dismissMilo(token, instanceId);
      setOpen(false);
      await refreshStatus();
    } catch {
      // Left showing: a refusal here is nothing the person can act on, and the next page load asks again.
    } finally {
      setBusy(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  // Never on the sign-in screen, and never where Milo is off: the button would open on a 404.
  if (!me || !status?.enabled) return null;

  const zIndex = 100;
  const pool = poolLine(status);
  // KEY-847: no Milo Plus is no longer no Milo — the chat always shows, with a note about what Plus adds.
  const plusNote = showsPlusNote(status) && !plusNoteHidden;
  const price = packPriceLine(status);
  const canBuy = !!me?.capabilities.includes("billing.read");

  if (!open) {
    return (
      <div style={{ position: "fixed", right: 16, bottom: 16, zIndex }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="kd-btn-primary"
          aria-label="Talk to Milo"
          title="Talk to Milo"
          style={{ boxShadow: "var(--shadow-lg)", borderRadius: 99 }}
        >
          <Icon name="sparkles" size={14} />
          Milo
        </button>
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-label="Milo"
      // Escape closes the panel and keeps the conversation: the server has it, and the button reopens it. While
      // talking, Escape first just stops talking (KEY-850).
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.stopPropagation();
        if (talk.on) talk.stop();
        else setOpen(false);
      }}
      style={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex,
        width: 400,
        maxWidth: "calc(100vw - 32px)",
        height: 560,
        maxHeight: "calc(100vh - 32px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: "var(--radius-lg)",
        border: "var(--border-w) solid var(--border)",
        background: "var(--surface)",
        boxShadow: "var(--shadow-lg)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 12px 12px 16px", borderBottom: "var(--border-w) solid var(--border)" }}>
        <Icon name="sparkles" size={16} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "var(--text)" }}>Milo</h2>
          {pool && <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{pool}</div>}
        </div>
        {talk.available && (
          <button
            type="button"
            onClick={() => (talk.on ? talk.stop() : talk.start())}
            aria-pressed={talk.on}
            aria-label={talk.available === "talk" ? "Talk to Milo hands-free" : "Read replies aloud"}
            title={talk.available === "talk" ? (talk.on ? "Stop talking (Esc)" : "Talk hands-free — Milo answers out loud") : talk.on ? "Stop reading replies aloud" : "Read replies aloud"}
            style={{
              width: 32,
              height: 32,
              borderRadius: 99,
              border: "none",
              background: talk.on ? "var(--accent)" : "var(--surface-inset)",
              color: talk.on ? "var(--on-accent)" : "var(--text-muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "none",
            }}
          >
            <Icon name={talk.available === "talk" ? "mic" : "chat"} size={16} />
          </button>
        )}
        {chat.messages.length > 0 && (
          <button
            type="button"
            onClick={startAfresh}
            disabled={busy}
            title="Start a new conversation"
            style={{ border: "none", background: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "2px 4px" }}
          >
            New
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close Milo"
          title="Close"
          style={{ width: 32, height: 32, borderRadius: 99, border: "none", background: "var(--surface-inset)", color: "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}
        >
          <Icon name="chevDown" size={16} />
        </button>
      </div>

      <div ref={listRef} style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        {plusNote && (
          // Free Milo (KEY-847): the everyday things work as they are; Milo Plus is for anything in your own words.
          <div className="kd-card" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8, fontSize: 13.5, lineHeight: 1.5 }}>
            <div style={{ fontWeight: 700, color: "var(--text)" }}>You're on free Milo</div>
            <div style={{ color: "var(--text-muted)" }}>
              Ask what's on, add things, mark them done, remind someone or find something — free. <strong>Milo Plus</strong> understands
              anything in your own words, and can plan from a letter or a photo{price ? `, from ${price}` : ""}.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              {instanceId && (
                <button type="button" className="kd-btn-secondary" onClick={() => void dismiss()} disabled={busy} title="Put Milo away on this dial, for everyone on it">
                  Turn Milo off here
                </button>
              )}
              <button type="button" className="kd-btn-secondary" onClick={() => setPlusNoteHidden(true)}>Not now</button>
              {canBuy && (
                <button type="button" className="kd-btn-primary" onClick={() => { setOpen(false); navigate("/settings"); }}>
                  Get Milo Plus
                </button>
              )}
            </div>
          </div>
        )}
        {chat.messages.length === 0 && (
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 13.5, lineHeight: 1.5 }}>
            Ask me what's on today, what's overdue, or to add something to a list. I'll check with you before I change a status or add an item.
          </p>
        )}
        {chat.messages.map((m, i) => (
          <div
            key={i}
            style={{
              alignSelf: m.role === "user" ? "flex-end" : "flex-start",
              maxWidth: "85%",
              padding: "9px 12px",
              borderRadius: 14,
              fontSize: 14,
              lineHeight: 1.45,
              whiteSpace: "pre-wrap",
              background: m.role === "user" ? "var(--accent)" : m.error ? "#fee2e2" : "var(--surface-2)",
              color: m.role === "user" ? "var(--on-accent)" : m.error ? "#991b1b" : "var(--text)",
            }}
          >
            {m.text}
          </div>
        ))}
        {chat.proposed && (
          <div className="kd-card" style={{ padding: 12, alignSelf: "stretch", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>Milo wants to</div>
            <div style={{ fontSize: 14, color: "var(--text)" }}>{chat.proposed.summary}</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button type="button" className="kd-btn-secondary" onClick={() => void answer(false)} disabled={busy}>Not now</button>
              {/* More than one calendar and nothing to pick between them: a button each, which is the yes. */}
              {chat.proposed.choices?.length
                ? chat.proposed.choices.map((c) => (
                    <button key={c.id} type="button" className="kd-btn-primary" onClick={() => void answer(true, c.id)} disabled={busy}>{c.label}</button>
                  ))
                : <button type="button" className="kd-btn-primary" onClick={() => void answer(true)} disabled={busy}>Do it</button>}
            </div>
          </div>
        )}
        {/* KEY-717: the month is spent and the last thing shown was a refusal — offer the way out, for whoever can buy. */}
        {!busy && canBuy && offersTopUp(status) && chat.messages.at(-1)?.error && (
          <div style={{ alignSelf: "flex-start", display: "flex", flexDirection: "column", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
            <span>300 more requests for £{((status?.topUpPence ?? 0) / 100).toFixed(2)} — whatever you don't use carries over.</span>
            <button type="button" className="kd-btn-secondary" style={{ alignSelf: "flex-start" }} onClick={() => { setOpen(false); navigate("/settings"); }}>
              Buy 300 more
            </button>
          </div>
        )}
        {busy && <div style={{ alignSelf: "flex-start", color: "var(--text-muted)", fontSize: 13 }}>Milo is thinking…</div>}
      </div>

      <ConversationLine talk={talk} />

      {(
      <div style={{ display: "flex", gap: 8, padding: 12, borderTop: "var(--border-w) solid var(--border)", alignItems: "flex-end" }}>
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            // Typing over Milo: he stops talking and lets you finish.
            talk.typing();
          }}
          onKeyDown={onKey}
          rows={2}
          placeholder={chat.proposed ? "Or say something else…" : "Ask Milo…"}
          aria-label="Say something to Milo"
          disabled={busy}
          style={fieldStyle}
        />
        <button
          type="button"
          className="kd-btn-primary"
          onClick={() => void send()}
          disabled={busy || !draft.trim()}
          aria-label="Send"
          title="Send (Enter)"
          style={{ padding: "10px 12px" }}
        >
          <Icon name="send" size={14} />
        </button>
      </div>
      )}
      {chat.turnsLeft !== null && chat.turnsLeft <= 5 && (
        <div style={{ padding: "0 12px 10px", fontSize: 11.5, color: "var(--text-muted)" }}>
          {chat.turnsLeft === 0 ? "This conversation is full — start a new one." : `${chat.turnsLeft} turns left in this conversation.`}
        </div>
      )}
    </div>
  );
}

/**
 * The live line above the input while conversation mode is on (KEY-850): what Milo is doing, what he is hearing,
 * and a Stop. When the mode went off on its own (quiet, no microphone, a refusal) it says why instead.
 */
function ConversationLine({ talk }: { talk: ReturnType<typeof useMiloConversation> }) {
  const { state } = talk;
  const status = !talk.on
    ? state.notice
    : state.kind === "read"
      ? state.phase === "speaking"
        ? "Reading the reply aloud…"
        : state.phase === "sending"
          ? "Milo is thinking…"
          : "Replies are read aloud."
      : state.phase === "sending"
        ? "Milo is thinking…"
        : state.phase === "speaking"
          ? "Milo is speaking…"
          : "Listening…";
  const hearing = talk.on && state.phase === "listening" ? [state.committed, state.sessionFinal, state.interim].filter(Boolean).join(" ") : "";
  return (
    <div
      style={{
        display: talk.on || state.notice ? "flex" : "none",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        borderTop: "var(--border-w) solid var(--border)",
        fontSize: 13,
        color: "var(--text-muted)",
      }}
    >
      {talk.on && <Icon name={state.kind === "talk" && state.phase === "listening" ? "mic" : state.kind === "talk" ? "micOff" : "chat"} size={14} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* The status is announced; the words being heard change too fast to be read out, so they aren't. */}
        <span role="status" aria-live="polite">{status}</span>
        {hearing && (
          <span data-testid="milo-hearing" style={{ display: "block", color: "var(--text)", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {hearing}
          </span>
        )}
      </div>
      {talk.on && (
        <button type="button" className="kd-btn-secondary" onClick={() => talk.stop()} title="Stop (Esc)" style={{ padding: "4px 10px", display: "flex", alignItems: "center", gap: 6 }}>
          <Icon name="stop" size={12} />
          Stop
        </button>
      )}
    </div>
  );
}
